import React, { PureComponent } from 'react'
import cx from 'classnames'
import { bool, func } from 'prop-types'
import isEmpty from 'lodash.isempty'
import { isMobile } from 'react-device-detect'
import { isIE, isEdge, isFirefox } from 'src/utils/helpers'
import { Popover, PopoverTrigger, PopoverContent, PopoverArrow } from '@chakra-ui/react'
import { Picker } from 'src/components/Emoji'
import QuillContent from 'src/components/Editor'

import Input from 'src/components/Form/Input/Input'
import FontAwesomeIconComponent from 'src/components/FontAwesomeIcon'

import styles from './styles.module.less'

const quillModules = {
  toolbar: false,
  keyboard: {
    bindings: {
      enter: {
        key: 13,
        handler: function() {
          // Do nothing
        },
      },
    },
  },
}

class ChatActions extends PureComponent {
  static propTypes = {
    onMessageSend: func,
    onLinkClick: func,
    enableAttachment: bool,
    resetUnreadStates: func,
  }

  static defaultProps = {
    enableAttachment: true,
  }

  quillRef = null
  unreadCountEventsTimeout = null

  state = {
    emojiVisible: false,
    attachmentAvailable: true,
    content: '',
    message: '',
    text: '',
    selection: 0,
  }

  setQuillRef = ref => {
    this.quillRef = ref

    if (this.quillRef && this.quillRef.editor) {
      this.quillRef.editor.root.classList.add('ql-editor--chat')
    }
  }

  handleSendMessage = (isPlain = false) => {
    const { text, message } = this.state

    if (!text.length) {
      return
    }

    this.setState(
      {
        message: '',
        text: '',
        content: '',
        selection: 0,
        attachmentAvailable: true,
      },
      () => this.props.onMessageSend(this.prepareForSubmit(message, isPlain)),
    )
  }

  handleEmojiPicker = emoji => {
    this.setState({ emojiVisible: false }, () => {
      const cursorPosition = this.state.selection
      if (this.quillRef) {
        // Add emoji
        this.quillRef.editor.insertText(cursorPosition, `${emoji.native}`, 'silent')
        // Place cursor after added emoji (emojis are usually 2 characters, but sometimes it's more)
        this.quillRef.editor.setSelection(cursorPosition + emoji.native.length, 'silent')
      } else {
        const currentText = this.state.text
        let newText = currentText

        // If there is some text, inject emoji in saved position (where cursor was)
        if (currentText && currentText.length > 0) {
          newText =
            currentText.slice(0, cursorPosition) + emoji.native + currentText.slice(cursorPosition)
        }

        this.setState({
          message: newText,
          text: newText,
          content: newText,
          attachmentAvailable: !newText,
        })
      }
    })
  }

  toggleEmojiPicker = () => {
    this.setState({ emojiVisible: !this.state.emojiVisible })
  }

  onKeyPress = e => {
    if (e.keyCode === 13 && !e.shiftKey) {
      const { text, message } = this.state

      if (!text.length) {
        return
      }

      this.setState(
        {
          message: '',
          text: '',
          content: '',
          attachmentAvailable: true,
        },
        () => {
          this.props.onMessageSend(this.prepareForSubmit(message))
        },
      )
    }
  }

  onEditorChange = (value, delta, source, editor) => {
    const text = editor && editor.getText().trim()

    this.resetUnreadStates()

    this.setState({
      message: value,
      text,
      content: editor && editor.getContents(),
      attachmentAvailable: !text.length,
    })
  }

  onEditorBlur = previousRange => {
    this.setState({
      selection: previousRange ? previousRange.index : 'none',
    })
  }

  onPlainTextKeyDown = e => {
    if (e.keyCode === 13 && !e.shiftKey) {
      const text = e.target.value

      if (!text.length) {
        return
      }

      this.setState(
        {
          message: '',
          text: '',
          content: '',
          attachmentAvailable: true,
        },
        () => {
          this.props.onMessageSend(this.prepareForSubmit(text, true))
        },
      )
    }
  }

  onPlainTextChange = e => {
    const text = e.target.value

    this.resetUnreadStates()

    this.setState({
      message: text,
      text: text,
      attachmentAvailable: !(text && text.length),
    })
  }

  onPlainTextBlur = e => {
    this.setState({
      selection: e.target.selectionStart || 'none',
    })
  }

  resetUnreadStates = () => {
    const { resetUnreadStates } = this.props

    if (typeof resetUnreadStates === 'function') {
      // Reset timeout on any change
      if (this.unreadCountEventsTimeout) {
        clearTimeout(this.unreadCountEventsTimeout)
      }

      // Execute updating unread counter every second, so it doesn't fire on every keystroke
      this.unreadCountEventsTimeout = setTimeout(function() {
        resetUnreadStates()
      }, 1000)
    }
  }

  prepareForSubmit = (message, isPlain = false) => {
    if (isPlain) {
      return [
        {
          type: 'paragraf',
          text: message,
        },
      ]
    }
    const divAccumulator = document.createElement('div')
    const messages = []
    divAccumulator.innerHTML = message
    divAccumulator.childNodes.forEach(node => {
      if (node.tagName === 'P') {
        messages.push({
          type: 'paragraf',
          text: node.innerText,
        })
      }
    })
    return messages
  }
  componentDidMount() {
    this.popoverRef = React.createRef()
    document.addEventListener('click', this.handleClickOutside)
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  }

  handleClickOutside = event => {
    if (this.popoverRef && !this.popoverRef.current.contains(event.target)) {
      this.setState({ ...this.state, emojiVisible: false })
    }
  }
  render() {
    const { message, attachmentAvailable, emojiVisible } = this.state
    const { enableAttachment, enableEmoji, isWidget, placeholder } = this.props

    // TODO: Remove plain text field when Quill is fixed for IE/Edge inside widget
    const isIeEdgeInWidget = (isIE() || isEdge()) && isWidget

    // Do not use emojis on mobile or if disabled from props
    const useEmojis = enableEmoji && !isMobile

    const chatActionsClassNames = cx({
      [styles.chatActions]: true,
    })
    return (
      <div className={styles.chatActionsHolder}>
        <div className={chatActionsClassNames} onClick={this.resetUnreadStates}>
          {isIeEdgeInWidget || (isFirefox() && isWidget) ? (
            <Input
              type='text'
              value={message}
              placeholder={placeholder}
              onChange={this.onPlainTextChange}
              onKeyDown={this.onPlainTextKeyDown}
              onBlur={this.onPlainTextBlur}
              onFocus={this.resetUnreadStates}
            />
          ) : (
            <QuillContent
              ref={this.setQuillRef}
              modules={quillModules}
              clipboard={{ matchVisual: false }}
              value={message}
              placeholder={placeholder}
              onKeyDown={this.onKeyPress}
              onChange={this.onEditorChange}
              onBlur={this.onEditorBlur}
              onFocus={this.resetUnreadStates}
            />
          )}
          <div
            // eslint-disable-next-line react/no-unknown-property
            display-if={useEmojis}
            style={{ opacity: 0, position: 'fixed' }}
          >
            <Picker onSelect={this.handleEmojiPicker} />
          </div>
          <div className={styles.actions} ref={this.popoverRef}>
            <div
              // eslint-disable-next-line react/no-unknown-property
              display-if={useEmojis}
              className={styles.actionItems}
            >
              <Popover isOpen={emojiVisible} placement='top-start'>
                <PopoverTrigger>
                  <div
                    className={styles.icon}
                    onClick={e => {
                      e.stopPropagation()
                      this.toggleEmojiPicker()
                    }}
                  >
                    <FontAwesomeIconComponent
                      icon={['far', 'face-smile']}
                      size='xs'
                      type='input-icon-color'
                    />
                  </div>
                </PopoverTrigger>
                <PopoverContent>
                  <PopoverArrow />
                  <Picker onSelect={this.handleEmojiPicker} isWidget={isWidget} />
                </PopoverContent>
              </Popover>
            </div>
            <div
              // eslint-disable-next-line react/no-unknown-property
              display-if={attachmentAvailable && enableAttachment}
              className={styles.actionItems}
            >
              <div className={styles.icon} onClick={this.props.onLinkClick}>
                <FontAwesomeIconComponent
                  icon={['far', 'paperclip']}
                  size='xs'
                  type='input-icon-color'
                />
              </div>
            </div>
            <div
              // eslint-disable-next-line react/no-unknown-property
              display-if={!attachmentAvailable && !isEmpty(message)}
              className={styles.actionItems}
              onClick={() => this.handleSendMessage(isIeEdgeInWidget)}
            >
              <div className={styles.icon}>
                <FontAwesomeIconComponent
                  icon={['far', 'paper-plane-top']}
                  size='xs'
                  type='input-icon-color'
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

export default ChatActions
